import { createSelector } from '@reduxjs/toolkit';
import { get, isEmpty, isNil, orderBy } from 'lodash';
import moment from 'moment';
import stable from 'stable';

import type {
  AdvancementVersion,
  RankVersion,
} from '@appTypes/common/versions';
import type { Rank } from '@modules/bulkEntry/duck/types';
import { payloadSel, typeSel } from '@modules/location/duck/selectors';
import { hasEditOptOutPerms } from '@modules/user/permissionChecks';
import { advancementTypes } from '@shared/constants';
import { ROUTE_PROFILE } from '@shared/duck/actions';
import {
  isParentGuardianSel,
  organizationPositionSel,
  ranksSel as rankDictSel,
  unitTypeIdSel,
} from '@shared/duck/selectors/general.selectors';

import type { RootState } from '../../../root/store';
import { advancementsStates, moduleName } from '../constants';
import { getAdvancementUid } from '../utils';
import reducers from './reducersTyped';
import {
  highestRankSel,
  memberDetailsSel,
  youthAdvRequirementsSel,
} from './selectors';

type RankStates = (typeof advancementsStates)[keyof typeof advancementsStates];
type RankWithState = Rank & { state: RankStates };

export const moduleSel = (state: RootState): ReturnType<typeof reducers> =>
  state[moduleName];

const youthAdvRequirementsParamsSel = (state: RootState) =>
  payloadSel(state) as Record<string, string>;

const advancementsVersionsSel = (state: RootState) =>
  moduleSel(state).advancementsVersions;

export const advReqEditLoadingSel = createSelector(
  moduleSel,
  ({ advReqEditLoading }) => advReqEditLoading,
);

export const advReqEditStopLoadingSel = createSelector(
  moduleSel,
  ({ advReqEditStopLoading }) => advReqEditStopLoading,
);

const getRankState = (rank: Rank, highestRank: Rank) => {
  if (!highestRank) return advancementsStates.NEXT;
  const highestRankLevel = Number(highestRank.level);
  const rankLevel = Number(rank.level);

  if (highestRankLevel > rankLevel) {
    return advancementsStates.PREVIOUS;
  } else if (highestRankLevel === rankLevel) {
    return advancementsStates.LAST_APPROVED;
  } else {
    return advancementsStates.NEXT;
  }
};

export const getRanks = ({
  ranksList,
  recordedRanks,
  highestRank,
}: {
  ranksList: Rank[];
  recordedRanks: Rank[];
  highestRank: Rank;
}): RankWithState[] =>
  ranksList
    .map((rank: Rank) => {
      const recordedRank = recordedRanks.find(({ id }) => id == rank.id);
      if (recordedRank) {
        return recordedRank;
      }
      return { ...rank, uid: getAdvancementUid(rank, advancementTypes.RANKS) };
    })
    .map((rank: Rank) => ({
      ...rank,
      state: getRankState(rank, highestRank),
    }));

export const unitRanksSel = createSelector(
  rankDictSel,
  unitTypeIdSel,
  (_state: RootState, unitTypeId: number) => unitTypeId,
  (ranks, unitTypeId, customUnitTypeId) => {
    const selectedProgramId = customUnitTypeId || unitTypeId;
    return ranks.filter(
      ({ programId }: { programId: string | number }) =>
        selectedProgramId == programId,
    );
  },
);

export const ranksSel = createSelector(
  [
    highestRankSel,
    memberDetailsSel,
    (state: RootState, unitTypeId: number) => unitRanksSel(state, unitTypeId),
  ],
  (highestRank, memberDetails, ranksList) => {
    const recordedRanks = memberDetails.ranks || [];
    const ranks = getRanks({ ranksList, recordedRanks, highestRank });
    const sortedRanks = stable(
      ranks,
      (a, b) => Number(a.level) > Number(b.level),
    );
    return sortedRanks;
  },
);

export const canEditOptOutSel = createSelector(
  isParentGuardianSel,
  organizationPositionSel,
  typeSel,
  (isParentGuardian, position, pageType) => {
    const isSelfProfile = pageType === ROUTE_PROFILE;
    const hasPermissions = hasEditOptOutPerms(position);
    const valid = hasPermissions || isParentGuardian || isSelfProfile;
    return valid;
  },
);

export const advancementsVersionsPerAdvTypeSel = createSelector(
  advancementsVersionsSel,
  youthAdvRequirementsParamsSel,
  (versions, params) => {
    const { advancementType, advancementId } = params;

    return get(
      versions,
      `${advancementType}-${advancementId}`,
      [] as AdvancementVersion[],
    );
  },
);

export const nonExpiredAdvVersionsSel = createSelector(
  youthAdvRequirementsSel,
  advancementsVersionsPerAdvTypeSel,
  (youthReqs, versions) => {
    const { versionId: activeVersionId } = youthReqs;
    const now = moment();

    const nonExpiredVersions = versions.filter(version => {
      const rankVersion = version as RankVersion;
      const lacksExpiredDate =
        isNil(rankVersion.expiredDate) || isEmpty(rankVersion.expiredDate);
      //expiredDate is the "scoutbook" expired date which controls whether we should hide the version
      const expiredDate = moment(rankVersion.expiredDate);

      return lacksExpiredDate || expiredDate.isAfter(now);
    });

    // also make an exception for rank versions that are already Active.
    const activeVersion = versions.find(
      version => version.versionId === activeVersionId,
    );
    const latestTwoVersions = nonExpiredVersions.slice(0, 2);

    const isActiveNotIntTwoLatest =
      activeVersion &&
      !latestTwoVersions.some(
        version => version.versionId === activeVersion.versionId,
      );

    return isActiveNotIntTwoLatest
      ? [...latestTwoVersions, activeVersion]
      : latestTwoVersions;
  },
);

export const versionExpiryDateSel = createSelector(
  youthAdvRequirementsSel,
  advancementsVersionsPerAdvTypeSel,
  (requirements, versions) => {
    const { versionId: activeVersionId } = requirements;
    const activeVersion = versions.find(
      version => version.versionId === activeVersionId,
    );
    const expiryDate = activeVersion ? activeVersion.versionExpiryDt : null;
    return expiryDate;
  },
);
export const versionEffectiveDateSel = createSelector(
  youthAdvRequirementsSel,
  advancementsVersionsPerAdvTypeSel,
  (requirements, versions) => {
    const { versionId: activeVersionId } = requirements;
    const activeVersion = versions.find(
      version => version.versionId === activeVersionId,
    );
    const effectiveDate = activeVersion
      ? activeVersion.versionEffectiveDt
      : null;
    return effectiveDate;
  },
);
export const newestVersionSel = createSelector(
  advancementsVersionsPerAdvTypeSel,
  versions => versions[0] || ({} as AdvancementVersion),
);
export const effectiveVersionSel = createSelector(
  advancementsVersionsPerAdvTypeSel,
  versions =>
    versions.find(version => {
      const versionStart = moment(version.versionEffectiveDt);
      const versionEnd = moment(version.versionExpiryDt);

      return (
        versionStart.isBefore(moment()) &&
        (versionEnd.isAfter(moment()) || !version.versionExpiryDt)
      );
    }) || ({} as AdvancementVersion),
);

export const sortedExpiredAdvVersionsSel = createSelector(
  advancementsVersionsPerAdvTypeSel,
  versions => {
    const now = moment();
    const expiredVersions = versions.filter(version => {
      const rankVersion = version as RankVersion;
      const hasExpiredDate = !isEmpty(rankVersion.expiredDate);
      const expiredDate = moment(rankVersion.expiredDate);

      return hasExpiredDate || expiredDate.isBefore(now);
    });

    return orderBy(expiredVersions, ['versionId'], ['desc']);
  },
);

export const isVersionArchivedSel = createSelector(
  sortedExpiredAdvVersionsSel,
  (_: RootState, versionId: number | string | null) => versionId,
  (expiredVersions, versionId) => {
    if (isNil(versionId) || expiredVersions.length < 2) return false;

    // Archived if versionId is in the expired list starting from the second element
    return expiredVersions
      .slice(1)
      .some(version => version.versionId === versionId);
  },
);
